import React from 'react';
import { useNavigate } from 'react-router-dom';

import styled from 'styled-components';

import ChevronRight from 'common/Icons/ChevronRight';
import ActionPlanStatusColumn from 'components/CommunityAIOverview/ActionPlanStatusColumn';
import LoaderRibbed from 'components/lib/LoaderRibbed';
import { ActionPlanCompanyPortfolioStatsType } from 'types/actionPlans';

import NeedsANudgeColumn from './NeedsANudgeColumn';

const COMPANY_ACTION_PLANS_PATH = '/action-plans-company';

const Container = styled.div`
  border-radius: 10px;
  border: solid 1px var(--line-gray);
  padding: 30px;
  padding-bottom: 0;
  margin-bottom: 30px;
  background-color: var(--white);
`;

const Header = styled.div`
  display: flex;
  flex-flow: row;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid var(--line-gray);
`;

const TitleGroup = styled.div`
  display: flex;
  flex-flow: row;
  gap: 10px;
`;

const ImageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-image: linear-gradient(to bottom, var(--lavender-frost), var(--lavender-frost-end));
  border-radius: 4px;
  height: 32px;
  width: 32px;
  flex-shrink: 0;
`;

const Title = styled.div`
  font-size: 20px;
  font-family: var(--font-bold);
`;

const Subtitle = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
`;

const ActionPlansButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  border: none;
  border-radius: 4px;
  background: linear-gradient(
    277deg,
    var(--competitor-purple),
    var(--aici-action-icon-gradient-mid) 50%,
    var(--flamingo-pink)
  );
  color: var(--white);
  font-family: var(--font-bold);
  font-size: 14px;
  padding: 10px 20px;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const Columns = styled.div`
  display: flex;
  flex-flow: row;

  & > * + * {
    border-left: solid 1px var(--line-gray);
  }
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 60px 0;
`;

type Props = {
  stats?: ActionPlanCompanyPortfolioStatsType;
  loading: boolean;
};

const ActionPlansPortfolioCard = ({ stats, loading }: Props) => {
  const navigate = useNavigate();

  const totalPlans = stats?.total_plans ?? 0;
  const totalCommunities = stats?.total_communities ?? 0;
  const communitiesNeedingAttention = stats?.communities_needing_attention ?? [];

  const actionPlansSummary = {
    completed_count: stats?.completed_count ?? 0,
    in_progress_count: stats?.in_progress_count ?? 0,
    not_started_count: stats?.not_started_count ?? 0,
    total_count: totalPlans,
    unassigned_count: stats?.unassigned_count ?? 0,
    overdue_count: stats?.overdue_count ?? 0,
  };

  const goToActionPlans = () => {
    navigate(COMPANY_ACTION_PLANS_PATH);
  };

  return (
    <Container data-test-id="company-ai-overview-action-plans-portfolio-card">
      <Header>
        <TitleGroup>
          <ImageContainer>
            <img src="/icons/checklist-blue.svg" width="18" height="18" alt="" />
          </ImageContainer>
          <div>
            <Title>Action Plans Across the Portfolio</Title>
            <Subtitle>
              {totalPlans} plans in {totalCommunities} communities · What needs a nudge this week
            </Subtitle>
          </div>
        </TitleGroup>
        <ActionPlansButton data-test-id="company-ai-overview-action-plans-button" onClick={goToActionPlans}>
          <img src="/icons/ai-blob-white.svg" alt="" width={20} height={20} /> Action Plans{' '}
          <ChevronRight fill="var(--white)" width={20} height={20} />
        </ActionPlansButton>
      </Header>
      {loading ? (
        <LoaderContainer>
          <LoaderRibbed active />
        </LoaderContainer>
      ) : (
        <Columns>
          <ActionPlanStatusColumn actionPlans={actionPlansSummary} actionPlansPath={COMPANY_ACTION_PLANS_PATH} />
          <NeedsANudgeColumn communities={communitiesNeedingAttention} />
        </Columns>
      )}
    </Container>
  );
};

export default ActionPlansPortfolioCard;
